import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePropertyOverviewQuery } from '@/hooks/queries/usePropertyDashboard';
import type { OverviewData } from '@/hooks/queries/usePropertyDashboard';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CircularProgress } from '@/components/ui/circular-progress';
import { Calendar, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';

type TrendData = OverviewData['trend_data'][0];

interface OverviewTabProps {
  propertyId?: string;
  TrendChartComponent?: React.ComponentType<{ data: TrendData[] }>;
}

export function OverviewTab({
  propertyId,
  TrendChartComponent,
}: OverviewTabProps) {
  const { t, i18n } = useTranslation();
  const [dateFilter, setDateFilter] = useState('');

  // Use React Query hook
  const { data: overviewData, isLoading } = usePropertyOverviewQuery(
    propertyId || '',
    dateFilter || undefined
  );

  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  const filteredDepartments = overviewData?.department_scores || [];
  const overallScore = overviewData?.overall_score || 0;
  const trendData = overviewData?.trend_data || [];
  const summaryStats = overviewData?.summary_stats;

  return (
    <div className={cn('space-y-6', isRTL && 'space-y-6')}>
      {/* Date Filter */}
      <Card>
        <CardHeader>
          <CardTitle
            className={cn('flex items-center gap-2', isRTL && 'text-right')}
          >
            <Filter className="h-4 w-4" />
            {t('properties.details.overviewTab.scoreOverviewFilter')}
          </CardTitle>
          <CardDescription className={cn(isRTL && 'text-right')}>
            {t('properties.details.overviewTab.filterDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={cn(
              'flex items-center gap-4',
              isRTL && 'flex-row-reverse'
            )}
          >
            <div className="flex-1 max-w-sm">
              <Label
                htmlFor="date-filter"
                className={cn(
                  'flex items-center gap-2 text-sm font-medium mb-2',
                  isRTL && 'text-right'
                )}
              >
                <Calendar className="h-3 w-3" />
                {t('properties.details.overviewTab.showAssessmentsFrom')}
              </Label>
              <Input
                id="date-filter"
                type="date"
                value={dateFilter}
                onChange={e => setDateFilter(e.target.value)}
                className={cn('w-full', isRTL && 'text-right')}
              />
            </div>
            {dateFilter && (
              <div
                className={cn(
                  'text-sm text-muted-foreground',
                  isRTL && 'text-right'
                )}
              >
                {t('properties.details.overviewTab.showingDepartments', {
                  count: filteredDepartments.length,
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Overall Score Circle */}
      <Card>
        <CardHeader className={cn('text-center', isRTL && 'text-center')}>
          <CardTitle>
            {t('properties.details.overviewTab.overallPropertyScore')}
          </CardTitle>
          <CardDescription>
            {dateFilter
              ? t('properties.details.overviewTab.averageScoreFiltered', {
                  date: new Date(dateFilter).toLocaleDateString(),
                })
              : t('properties.details.overviewTab.averageScore')}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center pb-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-[180px]">
              <div className="text-muted-foreground">{t('loading')}</div>
            </div>
          ) : (
            <CircularProgress
              value={overallScore}
              size={180}
              strokeWidth={12}
              label={t('properties.details.overviewTab.overallPropertyScore')}
              className="mb-4"
            />
          )}
        </CardContent>
      </Card>

      {/* Performance Trend Chart */}
      {TrendChartComponent && (
        <Card>
          <CardHeader>
            <CardTitle className={cn(isRTL && 'text-right')}>
              {t('properties.details.overviewTab.performanceTrend')}
            </CardTitle>
            <CardDescription className={cn(isRTL && 'text-right')}>
              {t('properties.details.overviewTab.trendDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-[200px]">
                <div className="text-muted-foreground">{t('loading')}</div>
              </div>
            ) : trendData.length > 0 ? (
              <TrendChartComponent data={trendData} />
            ) : (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                {t('properties.details.overviewTab.noTrendData')}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Department Scores */}
      <Card>
        <CardHeader>
          <CardTitle className={cn(isRTL && 'text-right')}>
            {t('properties.details.overviewTab.departmentScores')}
          </CardTitle>
          <CardDescription className={cn(isRTL && 'text-right')}>
            {dateFilter
              ? t('properties.details.overviewTab.departmentScoresFiltered', {
                  date: new Date(dateFilter).toLocaleDateString(),
                })
              : t('properties.details.overviewTab.departmentScoresDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">{t('loading')}</div>
            </div>
          ) : filteredDepartments.length === 0 ? (
            <div
              className={cn(
                'text-center py-12 text-muted-foreground',
                isRTL && 'text-center'
              )}
            >
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">
                {t('properties.details.overviewTab.noDepartmentsFound')}
              </p>
              <p>{t('properties.details.overviewTab.noDepartmentsDesc')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredDepartments.map(department => (
                <div
                  key={department.id}
                  className="flex flex-col items-center space-y-3"
                >
                  <CircularProgress
                    value={department.score}
                    size={100}
                    strokeWidth={6}
                    showValue={true}
                  />
                  <div className={cn('text-center', isRTL && 'text-center')}>
                    <p className="font-medium text-sm">{department.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('properties.details.overviewTab.lastAssessment', {
                        date: new Date(
                          department.last_assessment_date
                        ).toLocaleDateString(),
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle
              className={cn('text-sm font-medium', isRTL && 'text-right')}
            >
              {t('properties.details.overviewTab.highestScore')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-2xl font-bold text-muted-foreground">--</div>
            ) : (
              <>
                <div
                  className={cn(
                    'text-2xl font-bold text-green-600',
                    isRTL && 'text-right'
                  )}
                >
                  {summaryStats && summaryStats.highest_score > 0
                    ? `${summaryStats.highest_score}%`
                    : t('properties.details.overviewTab.noData')}
                </div>
                <p
                  className={cn(
                    'text-xs text-muted-foreground',
                    isRTL && 'text-right'
                  )}
                >
                  {summaryStats?.highest_department ||
                    t('properties.details.overviewTab.noData')}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle
              className={cn('text-sm font-medium', isRTL && 'text-right')}
            >
              {t('properties.details.overviewTab.lowestScore')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-2xl font-bold text-muted-foreground">--</div>
            ) : (
              <>
                <div
                  className={cn(
                    'text-2xl font-bold text-red-600',
                    isRTL && 'text-right'
                  )}
                >
                  {summaryStats && summaryStats.lowest_score > 0
                    ? `${summaryStats.lowest_score}%`
                    : t('properties.details.overviewTab.noData')}
                </div>
                <p
                  className={cn(
                    'text-xs text-muted-foreground',
                    isRTL && 'text-right'
                  )}
                >
                  {summaryStats?.lowest_department ||
                    t('properties.details.overviewTab.noData')}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle
              className={cn('text-sm font-medium', isRTL && 'text-right')}
            >
              {t('properties.details.overviewTab.scoreRange')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-2xl font-bold text-muted-foreground">--</div>
            ) : (
              <>
                <div
                  className={cn(
                    'text-2xl font-bold text-blue-600',
                    isRTL && 'text-right'
                  )}
                >
                  {summaryStats && summaryStats.score_range >= 0
                    ? `${summaryStats.score_range}%`
                    : t('properties.details.overviewTab.noData')}
                </div>
                <p
                  className={cn(
                    'text-xs text-muted-foreground',
                    isRTL && 'text-right'
                  )}
                >
                  {t('properties.details.overviewTab.differenceHighestLowest')}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
